/**
 * Every site ever scanned, as a work surface.
 *
 * The column order is the order the questions get asked on a call: how bad is
 * it, whose site is it, what is it built on, how far behind is that, can we
 * reach them, and when did we last look. The version cell is the one that earns
 * the list - see `VersionCell.tsx`.
 *
 * Nine table columns became seven pill tracks. The two "when did we last look"
 * columns folded into one: the manual check on top, the robot's underneath,
 * because they are the same question asked of two different actors.
 *
 * The row opens a scan in place rather than navigating, so the domain is a
 * `<button>` with a stretched overlay. That it is a real control is the point -
 * the old `<tr onClick>` could not be reached by keyboard at all.
 */
import { Bot, Building2, ChevronLeft, ChevronRight, Mail, ScanLine } from "lucide-react";
import { cn } from "@wa/ui";
import {
  BAND_DOT, formatEurShort, formatWhen, scoreBand,
  type ScanListItem, type ScanSort,
} from "../../../../lib/siteScan";
import type { CallTarget } from "../../../telephony/callState";
import { PhoneLink } from "../../../telephony/PhoneLink";
import { QueuePick, QueuePickAll, type QueuePicks } from "../../../telephony/queue";
import { ContextMenu, useContextMenu } from "../../../shell/ContextMenu";
import { Btn, EmptyState, SortHead, StatusPill, fmtDateTime, listCard } from "../../../ui";
import type { T } from "../types";
import { ScanRowItems } from "./ScanRowItems";
import { ScanRowMenu } from "./ScanRowMenu";
import { VersionCell } from "./VersionCell";

/**
 * Score, site, company, platform, outdated, contact, checked.
 *
 * The site track is the widest because it carries the domain AND up to three
 * condition pills (http, no mobile, dead). Sized off the domain alone it
 * truncated "ehitusekspert.ee" to "eh...".
 */
const COLS = ".6fr minmax(0,2.4fr) minmax(0,1.4fr) minmax(0,1.2fr) .65fr minmax(0,1.15fr) .8fr 44px";
/** The same tracks with the call-list checkbox in front. Only ever drawn for
 *  an operator who can place calls - see `useQueuePicks`. */
const PICK_COLS = `34px ${COLS}`;

/** What dialling this row means. Null when there is nothing to dial - the row
 *  still gets a (disabled) box, so the column has no holes in it. */
function targetOf(row: ScanListItem): CallTarget | null {
  if (!row.phone) return null;
  return {
    phone: row.phone,
    source: "scanner",
    companyName: row.companyName ?? undefined,
    regCode: row.regCode ?? undefined,
    leadDomain: row.domain,
  };
}


export function ScansTable({
  items,
  loading,
  total,
  page,
  pageSize,
  sort,
  order,
  onSort,
  onPage,
  onOpen,
  onRescan,
  onDelete,
  canManage,
  picks,
  t,
}: {
  items: ScanListItem[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  sort: ScanSort;
  order: "asc" | "desc";
  onSort: (c: ScanSort) => void;
  onPage: (p: number) => void;
  onOpen: (domain: string) => void;
  /** Refetch the site rather than reading the stored scan. */
  onRescan: (domain: string) => void;
  onDelete: (domain: string) => void;
  canManage: boolean;
  /** The call-list selection, or null for anyone who cannot place calls - in
   *  which case the column is not drawn at all. */
  picks: QueuePicks | null;
  t: T;
}) {
  const menu = useContextMenu<ScanListItem>();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const cols = picks ? PICK_COLS : COLS;
  const pageTargets = items.flatMap((row) => {
    const target = targetOf(row);
    return target ? [{ key: row.domain, target }] : [];
  });

  if (loading && items.length === 0) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <ScanLine className="size-6 animate-pulse" />
      </div>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <div className="crm-card">
        <EmptyState icon={ScanLine} title={t("scanner.table.empty")} body={t("scanner.table.emptyBody")} />
      </div>
    );
  }

  return (
    <div className="crm-card" style={listCard(cols)}>
      <div className="crm-list-head" style={{ gridTemplateColumns: cols, paddingTop: 22 }}>
        {picks ? <QueuePickAll picks={picks} entries={pageTargets} /> : null}
        <SortHead label={t("scanner.table.col.score")} column="score" sort={sort} order={order} onSort={onSort} />
        <SortHead label={t("scanner.table.col.site")} column="domain" sort={sort} order={order} onSort={onSort} />
        <SortHead label={t("scanner.table.col.company")} column="company" sort={sort} order={order} onSort={onSort} />
        <SortHead label={t("scanner.table.col.platform")} column="cms" sort={sort} order={order} onSort={onSort} />
        <SortHead label={t("scanner.table.col.outdated")} column="outdated" sort={sort} order={order} onSort={onSort} />
        <span>{t("scanner.table.col.contact")}</span>
        <SortHead label={t("scanner.table.col.checked")} column="checked" sort={sort} order={order} onSort={onSort} />
        <span />
      </div>

      <div className="crm-rows pt-0">
        {items.map((row) => {
          const band = scoreBand(row.opportunityScore ?? 0);
          const dead = row.status == null || row.status >= 400;
          return (
            <div
              key={row.domain}
              className="crm-list-row relative"
              style={{ gridTemplateColumns: cols }}
              onContextMenu={(e) => menu.open(e, row)}
            >
              {picks ? <QueuePick picks={picks} id={row.domain} target={targetOf(row)} /> : null}

              {/* The score is the weighted one. When the profile moved it, the
                  title says by how much - otherwise the number quietly
                  disagrees with the findings on the row's own scan page. */}
              <span
                className="flex items-center gap-1.5 pl-3"
                title={
                  row.defectScore != null && row.defectScore !== row.opportunityScore
                    ? t("scanner.table.scoreTitle", {
                        defect: row.defectScore,
                        criticality: row.criticality ?? 0,
                      })
                    : undefined
                }
              >
                <span className={cn("size-2 shrink-0 rounded-full", BAND_DOT[band])} />
                <span className="crm-tnum text-[13.25px] font-bold">{row.opportunityScore ?? 0}</span>
              </span>

              <span className="min-w-0">
                {/* Wraps: a site can carry three condition pills at once, and on
                    a narrow window they belong under the domain rather than
                    squeezing it to two characters. */}
                <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                  {/* Opens the scan in place, so a button rather than a link -
                      but a real control either way. */}
                  <button
                    type="button"
                    onClick={() => onOpen(row.domain)}
                    className="crm-prow-n crm-trunc min-w-0 text-left after:absolute after:inset-0 after:rounded-full"
                  >
                    {row.domain}
                  </button>
                  {dead ? (
                    <StatusPill tone="lt">{row.status ?? t("scanner.table.noAnswer")}</StatusPill>
                  ) : (
                    <>
                      {/* What the site is for, ahead of what is wrong with it:
                          it is the fact that decides whether the rest matters. */}
                      {/* A confirmed compromise goes first, ahead of what the
                          site is for: it is the only thing on this row that is
                          happening right now. Only `confirmed` gets a pill - a
                          suspicion is a filter and a finding line, not a label
                          in a list somebody scans at a glance. No new column:
                          `COLS` and `PICK_COLS` are two grid tracks that must
                          stay in step with the header, and a fourth pill costs
                          nothing. */}
                      {row.malwareVerdict === "confirmed" ? (
                        <StatusPill tone="lt">{t("scanner.table.filter.malwareConfirmed")}</StatusPill>
                      ) : null}
                      {row.siteKind && row.siteKind !== "unknown" ? (
                        <StatusPill tone="mut">{t(`scanner.kind.${row.siteKind}`)}</StatusPill>
                      ) : null}
                      {!row.https ? <StatusPill tone="wt">http</StatusPill> : null}
                      {!row.mobileReady ? <StatusPill tone="wt">{t("scanner.table.noMobileShort")}</StatusPill> : null}
                    </>
                  )}
                </span>
                {row.title ? <span className="crm-prow-s crm-trunc block">{row.title}</span> : null}
              </span>

              <span className="min-w-0">
                {row.companyName ? (
                  <>
                    <span className="flex min-w-0 items-center gap-1.5">
                      <Building2 className="crm-mut size-3.5 shrink-0" />
                      <span className="crm-trunc text-[13px]">{row.companyName}</span>
                    </span>
                    <span className="crm-prow-s crm-trunc flex items-center gap-2">
                      {row.regCode ? <span className="crm-tnum">{row.regCode}</span> : null}
                      {row.revenueCents != null ? <span>{formatEurShort(row.revenueCents)} EUR</span> : null}
                      {row.employees != null ? <span>{row.employees}</span> : null}
                    </span>
                  </>
                ) : (
                  <span className="crm-mut text-[12.5px]">-</span>
                )}
              </span>

              <VersionCell row={row} t={t} />

              <span className="crm-tnum flex items-center gap-1.5 text-[13px]">
                {row.outdated == null ? (
                  <span className="crm-mut text-[12.5px]">-</span>
                ) : (
                  <>
                    <span className={cn("font-bold", row.outdated > 0 ? "text-amber-600" : "text-emerald-600")}>
                      {row.outdated}
                    </span>
                    {row.abandoned != null && row.abandoned > 0 ? (
                      <span
                        title={t("scanner.table.abandonedTitle", { count: row.abandoned })}
                        className="rounded bg-red-500/10 px-1 text-[10px] text-red-600"
                      >
                        {row.abandoned}
                      </span>
                    ) : null}
                    {row.pluginCount != null ? <span className="crm-mut text-[10px]">/{row.pluginCount}</span> : null}
                  </>
                )}
              </span>

              <span className="flex min-w-0 flex-col gap-0.5">
                {row.email ? (
                  <a
                    href={`mailto:${row.email}`}
                    // Above the row-covering overlay, or the mail link is dead.
                    className="crm-trunc relative z-[1] inline-flex items-center gap-1 text-[12.5px] text-[var(--color-primary)] hover:underline"
                  >
                    <Mail className="size-3 shrink-0" />
                    <span className="crm-trunc">{row.email}</span>
                  </a>
                ) : null}
                {row.phone ? (
                  <PhoneLink
                    phone={row.phone}
                    source="scanner"
                    companyName={row.companyName ?? undefined}
                    regCode={row.regCode ?? undefined}
                    leadDomain={row.domain}
                    stopPropagation
                    className="crm-trunc relative z-[1] text-[12.5px]"
                  />
                ) : null}
                {!row.email && !row.phone ? <span className="crm-mut text-[12.5px]">-</span> : null}
              </span>

              {/* Two actors, one question. The person's last look on top, the
                  robot's underneath - they used to be two columns saying the
                  same word twice. */}
              <span className="crm-mut crm-tnum min-w-0 text-[12.5px]">
                <span className="crm-trunc block">{formatWhen(row.checkedAt)}</span>
                {row.autoCheckedAt ? (
                  <span
                    className="crm-prow-s crm-trunc flex items-center gap-1"
                    title={fmtDateTime(row.autoCheckedAt)}
                  >
                    <Bot className="size-3 shrink-0 text-[var(--color-primary)] opacity-70" />
                    {formatWhen(row.autoCheckedAt)}
                  </span>
                ) : (
                  <span className="crm-prow-s crm-trunc block">{t("scanner.table.neverAuto")}</span>
                )}
              </span>

              <ScanRowMenu
                row={row}
                canManage={canManage}
                t={t}
                onOpen={() => onOpen(row.domain)}
                onRescan={() => onRescan(row.domain)}
                onDelete={() => onDelete(row.domain)}
              />
            </div>
          );
        })}
      </div>

      {pages > 1 ? (
        <div className="crm-tfoot">
          {t("scanner.table.page", { page, pages })}
          <span className="crm-push flex gap-2.5">
            <Btn small disabled={page <= 1} onClick={() => onPage(page - 1)}>
              <ChevronLeft className="size-4" />
              {t("common.prev")}
            </Btn>
            <Btn small disabled={page >= pages} onClick={() => onPage(page + 1)}>
              {t("common.next")}
              <ChevronRight className="size-4" />
            </Btn>
          </span>
        </div>
      ) : null}

      {/* The row already opens its scan on a left click, so the right-click
          menu is where everything else about that site lives - refetch it,
          visit it, take its contact details, drop the row. */}
      <ContextMenu menu={menu} label={t("scanner.table.rowMenu")}>
        {(row, close) => (
          <ScanRowItems
            row={row}
            canManage={canManage}
            t={t}
            close={close}
            head
            onOpen={() => onOpen(row.domain)}
            onRescan={() => onRescan(row.domain)}
            onDelete={() => onDelete(row.domain)}
          />
        )}
      </ContextMenu>
    </div>
  );
}
